<template>
  <v-container fluid fill-height class="activate-account-page pa-0 ma-0">
    <div class="language-switcher">
      <v-menu offset-y left>
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            small
            outlined
            color="blue-grey darken-3"
            class="text-none language-switcher__button"
            v-bind="attrs"
            v-on="on"
          >
            <v-icon left small>mdi-translate</v-icon>
            {{ currentLanguageLabel }}
          </v-btn>
        </template>

        <v-list dense>
          <v-list-item
            v-for="language in languages"
            :key="language.code"
            @click="changeLanguage(language.code)"
          >
            <v-list-item-title>
              {{ $t(language.label) }}
            </v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
    </div>
    <v-row align="center"
    class="activate-account-content"
    justify="center">
      <v-col cols="12" md="6" lg="5" xl="4">
        <v-img
          :src="require('../../assets/xici-black.svg')"
          class="my-3 d-block"
          contain
          height="90"
        />

        <v-card
          class="elevation-12 mt-8 mb-16 pb-3"
          @keydown.enter="activateAccount"
          :loading="currently_loading"
        >

          <v-card-title class="white--text pink darken-4">
            {{$t('section.activateAccount')}}
          </v-card-title>

          <v-card-subtitle class="white--text text-left mb-6 pink darken-4" v-html="$t('info.alert.activateAccount')">
            {{$t('info.alert.activateAccount')}}

          </v-card-subtitle>

          <v-card-text>
            <v-form
              ref="activateAccountForm"
            >
              <v-text-field
                v-model="activation_token"
                :label="$t('form.activation_token')"
                name="activation_token"
                prepend-icon="mdi-shield-key"
                type="text"
                class="mb-3"
                :disabled="currently_loading"
                :hint="$t('hint.activationToken')"
                persistent-hint
              >
                <template v-slot:message="{ message }">
                 <span v-html="message"></span>
                </template>
              </v-text-field>

              <v-text-field
                v-model="new_password"
                id="new_password"
                :label="$t('form.new_password')"
                name="new_password"
                prepend-icon="mdi-key-variant"
                :append-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
                :type="showPassword ? 'text' : 'password'"
                @click:append="showPassword = !showPassword"
                class="mt-2 mb-1"
                :disabled="currently_loading"
                hide-details
              />
              <div
                class="password-hint-text password-hint-block mb-3"
              >
                <span v-html="$t('hint.password')"></span>
                <span>{{ " " }}</span>
                <v-tooltip
                  v-model="generate_password_tooltip_visible"
                  bottom
                  :open-on-hover="false"
                  :open-on-focus="false"
                >
                  <template v-slot:activator="{ attrs }">
                    <button
                      type="button"
                      class="password-hint-link"
                      :disabled="currently_loading"
                      v-bind="attrs"
                      @click="generateAndCopyPassword"
                    >
                      {{ $t("hint.password_generate_link") }}
                    </button>
                  </template>
                  <span>{{ $t(generate_password_tooltip_text_key) }}</span>
                </v-tooltip>
              </div>
              <div class="password-score-box mt-3" :class="`password-score-box--${passwordScoreType}`">
                <div class="password-score-header">
                  <strong>{{ $t("passwordScore.score", { score: visualPasswordScore }) }}</strong>
                  <span v-if="checking_password_score" class="password-score-checking">
                    {{ $t("passwordScore.checking") }}
                  </span>
                </div>
                <div class="password-score-bars" aria-hidden="true">
                  <span
                    v-for="index in 3"
                    :key="index"
                    class="password-score-bar"
                    :class="{ 'password-score-bar--active': index <= visualPasswordScore }"
                  ></span>
                </div>
                <div class="password-score-reasons">
                  {{ passwordScoreMessage }}
                </div>
              </div>
            </v-form>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn
              @click="activateAccount"
              class="px-4 mx-2"
              color="primary"
              :disabled="isSubmitBlocked"
            >
              <span :class="{ 'button-text-disabled-strike': isSubmitBlocked }">
                {{$t('button.activateAccount')}}
              </span>
            </v-btn>
          </v-card-actions>
          <v-expand-transition>
            <div v-show="password_changed">
              <v-divider></v-divider>
              <v-card-text
                v-html="'<span class=\'mdi mdi-checkbox-marked-circle pr-2\'></span>' + $t('hint.redirect_after_change_password', {'seconds':seconds_to_redirect})"
                class="font-italic blue-grey--text darken-3"
                style="font-size:110%;">
              </v-card-text>
            </div>
          </v-expand-transition>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import axios from "axios";
import debounce from "lodash/debounce";
import { SUPPORTED_LOCALES } from "../../utils/locale";
export default {
  data() {
    return {
      activation_token: this.$route.query.token || "",
      new_password: "",
      fromRoute: undefined,
      showPassword: false,
      currently_loading: false,
      password_changed: false,
      seconds_to_redirect: 8,
      password_score: null,
      password_score_reasons: [],
      checking_password_score: false,
      password_score_request_id: 0,
      debounced_password_check: null,
      generate_password_tooltip_visible: false,
      generate_password_tooltip_text_key: "info.alert.password_generated_copied",
      generate_password_tooltip_timeout: null,
      languages: [
        { code: "gl", label: "language.galician" },
        { code: "es", label: "language.spanish" },
        { code: "en", label: "language.english" }
      ]
    };
  },
  computed: {
    currentLanguageLabel() {
      const currentLanguage = this.languages.find(language => language.code === this.$i18n.locale);
      return currentLanguage ? this.$t(currentLanguage.label) : this.$i18n.locale.toUpperCase();
    },
    visualPasswordScore() {
      return Number.isInteger(this.password_score) ? this.password_score : 0;
    },
    passwordScoreType() {
      if (this.visualPasswordScore === 3) {
        return "success";
      }
      if (this.visualPasswordScore === 2) {
        return "info";
      }
      if (this.visualPasswordScore === 1) {
        return "warning";
      }
      return "error";
    },
    translatedScoreReasons() {
      return this.password_score_reasons
        .map(reason => {
          const key = `passwordScore.reasons.${reason}`;
          return this.$te(key) ? this.$t(key) : reason;
        })
        .join(" · ");
    },
    passwordScoreMessage() {
      if (this.checking_password_score) {
        return this.$t("passwordScore.checking");
      }
      if (this.password_score !== null && this.password_score_reasons.length === 0) {
        return "";
      }
      if (this.password_score_reasons.length) {
        return this.translatedScoreReasons;
      }
      return this.$t("passwordScore.not_checked");
    },
    isSubmitBlocked() {
      return this.currently_loading || this.checking_password_score || this.visualPasswordScore < 1;
    }
  },
  created() {
    this.debounced_password_check = debounce(this.checkPasswordAcceptable, 300);
  },
  beforeDestroy() {
    if (this.debounced_password_check && this.debounced_password_check.cancel) {
      this.debounced_password_check.cancel();
    }
    if (this.generate_password_tooltip_timeout) {
      clearTimeout(this.generate_password_tooltip_timeout);
    }
  },
  beforeRouteEnter(to, from, next) {
    next(vm => {
      vm.fromRoute = from.name;
    });
  },
  watch: {
    new_password() {
      this.debounced_password_check();
    },
    activation_token() {
      if (this.new_password) {
        this.debounced_password_check();
      }
    }
  },

  methods: {
    changeLanguage(locale) {
      if (!SUPPORTED_LOCALES.includes(locale)) {
        return;
      }

      this.$i18n.locale = locale;
      this.$store.dispatch("setLanguage", locale);
    },
    postPublicJson: async function(path, payload) {
      const response = await fetch(path, {
        method: "POST",
        credentials: "omit",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json"
        },
        body: JSON.stringify(payload)
      });

      let responseData = {};
      try {
        responseData = await response.json();
      } catch (error) {
        responseData = {};
      }

      if (!response.ok) {
        const requestError = new Error("Request failed");
        requestError.response = {
          status: response.status,
          data: responseData
        };
        throw requestError;
      }

      return responseData;
    },
    handleBack() {
      if (this.fromRoute) {
        this.$router.back();
      } else {
        this.$router.push("/");
      }
    },
    activateAccount: async function() {
      let activation_token = this.activation_token;
      let new_password = this.new_password;
      this.currently_loading = true;

      try {
        await this.postPublicJson(
          this.$store.state.endpoint + "/account/activate",
          {"activation_token": activation_token, "new_password": new_password}
        );
        this.password_changed = true;
        setTimeout(() => this.redirectionCountDown(), 1000);
      } catch (err) {
        let response = (err.response && err.response.data) ? err.response.data : {"reason": "error_500"};
        if(response.detail !== undefined && response.detail == "Invalid Token"){
          response.reason = "old_cookies";
        }
        let text = "";
        switch(response.reason){
          case "password_pwnd_stop_using":
            break;
          case "old_cookies":
          case "invalid_token":
          case "password_not_acceptable":
          case "expired_token":
            if (response.reject_reason === "pwndpassword"){
              text = this.$t("info.alert.password_pwnd_stop_using");
            }
            else{
              text = this.$t(`info.alert.${response.reason}`);
            }
            break;
          case "error_500":
            text = "Password is not acceptable. Please try another password.";
            break;
          default:
            text = `Unknown error: ${response.reason}`;
            break;
        }
        this.$store.commit("alert", {
          color: "error",
          text: text
        });
        setTimeout(() => this.currently_loading = false, 3000);
      }
    },
    randomIntInclusive: function(min, max) {
      return Math.floor(Math.random() * (max - min + 1)) + min;
    },
    generatePassword: function() {
      const upper = "ABCDEFGHJKLMNPQRSTUVWXYZ";
      const lower = "abcdefghijkmnopqrstuvwxyz";
      const digits = "123456789";
      const symbols = ".-,#!?@";
      const allChars = upper + lower + digits + symbols;
      const length = this.randomIntInclusive(14, 16);

      const pick = (charset) => charset[this.randomIntInclusive(0, charset.length - 1)];
      const generated = [
        pick(upper),
        pick(lower),
        pick(digits),
        pick(symbols)
      ];

      while (generated.length < length) {
        generated.push(pick(allChars));
      }

      for (let i = generated.length - 1; i > 0; i--) {
        const j = this.randomIntInclusive(0, i);
        [generated[i], generated[j]] = [generated[j], generated[i]];
      }

      return generated.join("");
    },
    copyToClipboard: async function(text) {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
        return;
      }

      const textArea = document.createElement("textarea");
      textArea.value = text;
      textArea.style.position = "fixed";
      textArea.style.opacity = "0";
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
    },
    generateAndCopyPassword: async function() {
      const generatedPassword = this.generatePassword();
      this.new_password = generatedPassword;
      this.showPassword = true;
      this.generate_password_tooltip_visible = false;
      if (this.generate_password_tooltip_timeout) {
        clearTimeout(this.generate_password_tooltip_timeout);
      }

      try {
        await this.copyToClipboard(generatedPassword);
        this.generate_password_tooltip_text_key = "info.alert.password_generated_copied";
      } catch (error) {
        this.generate_password_tooltip_text_key = "info.alert.password_generated_not_copied";
      }
      this.generate_password_tooltip_visible = true;
      this.generate_password_tooltip_timeout = setTimeout(() => {
        this.generate_password_tooltip_visible = false;
      }, 4500);
    },
    checkPasswordAcceptable: async function() {
      if (!this.new_password) {
        this.checking_password_score = false;
        this.password_score = null;
        this.password_score_reasons = [];
        return;
      }

      if (!this.activation_token || this.activation_token.length < 20) {
        this.checking_password_score = false;
        this.password_score = 0;
        this.password_score_reasons = ["invalid_token"];
        return;
      }

      const requestId = ++this.password_score_request_id;
      this.checking_password_score = true;

      try {
        const response = await this.postPublicJson(
          this.$store.state.endpoint + "/account/passwordAcceptable",
          {"activation_token": this.activation_token, "new_password": this.new_password}
        );
        if (requestId !== this.password_score_request_id) {
          return;
        }
        this.password_score = Number.isInteger(response.score) ? response.score : 0;
        this.password_score_reasons = Array.isArray(response.reasons) ? response.reasons : [];
      } catch (err) {
        if (requestId !== this.password_score_request_id) {
          return;
        }
        const response = (err.response && err.response.data) ? err.response.data : {};
        this.password_score = Number.isInteger(response.score) ? response.score : 0;
        this.password_score_reasons = Array.isArray(response.reasons) && response.reasons.length > 0
          ? response.reasons
          : ["score_check_failed"];
      } finally {
        if (requestId === this.password_score_request_id) {
          this.checking_password_score = false;
        }
      }
    },
    redirectionCountDown: function(){
      if(this.seconds_to_redirect>0){
        this.seconds_to_redirect--;
        setTimeout(() => this.redirectionCountDown(), 1000);
        if(this.seconds_to_redirect==0){
          this.$refs.activateAccountForm.reset();
        }
      }
      else{
        window.location.href = "https://hub.citius.gal/";
      }
    }
  }
};
</script>

<style scoped>
.activate-account-page {
  position: relative;
  overflow: hidden;
  width: 100vw;
  max-width: 100vw !important;
  min-height: 100vh;
  padding: 0 !important;
  margin: 0 !important;
  background-color: #f4f5f7;
}

.language-switcher {
  position: absolute;
  top: 16px;
  right: 16px;
  z-index: 2;
}

.language-switcher__button {
  background: rgba(255, 255, 255, 0.92);
  backdrop-filter: blur(8px);
}

.activate-account-page::before,
.activate-account-page::after {
  content: "";
  position: absolute;
  inset: 0;
  pointer-events: none;
}

.activate-account-page::before {
  opacity: 0.11;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='240' height='132' viewBox='0 0 240 132'%3E%3Cg fill='none' stroke='%2367737f' stroke-width='0.85' stroke-linecap='butt'%3E%3Cpath d='M-120 24 Q -90 8 -60 24 T0 24 T60 24 T120 24 T180 24 T240 24 T300 24 T360 24'/%3E%3Cpath d='M-120 66 Q -90 50 -60 66 T0 66 T60 66 T120 66 T180 66 T240 66 T300 66 T360 66'/%3E%3Cpath d='M-120 108 Q -90 92 -60 108 T0 108 T60 108 T120 108 T180 108 T240 108 T300 108 T360 108'/%3E%3C/g%3E%3C/svg%3E");
  background-size: 240px 132px;
  background-repeat: repeat;
  animation: waveShiftA 32s linear infinite;
}

.activate-account-page::after {
  opacity: 0.06;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='240' height='132' viewBox='0 0 240 132'%3E%3Cg fill='none' stroke='%2356616d' stroke-width='0.75' stroke-linecap='butt'%3E%3Cpath d='M-120 44 Q -90 60 -60 44 T0 44 T60 44 T120 44 T180 44 T240 44 T300 44 T360 44'/%3E%3Cpath d='M-120 88 Q -90 104 -60 88 T0 88 T60 88 T120 88 T180 88 T240 88 T300 88 T360 88'/%3E%3C/g%3E%3C/svg%3E");
  background-size: 240px 132px;
  background-repeat: repeat;
  background-position: 120px 22px;
  animation: waveShiftB 44s linear infinite reverse;
}

.activate-account-content {
  position: relative;
  z-index: 1;
}

.password-score-box {
  border: none;
  border-radius: 8px;
  background: #ffffff;
  padding: 10px 12px;
  min-height: 96px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

.password-score-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 0.9rem;
}

.password-score-checking {
  font-size: 0.8rem;
  opacity: 0.8;
}

.password-score-bars {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 6px;
  margin: 8px 0;
}

.password-score-bar {
  height: 8px;
  border-radius: 999px;
  background: #d9dde2;
  transition: background-color 0.2s ease;
}

.password-score-box--error .password-score-bar--active {
  background: #d32f2f;
}

.password-score-box--warning .password-score-bar--active {
  background: #ef6c00;
}

.password-score-box--info .password-score-bar--active {
  background: #1976d2;
}

.password-score-box--success .password-score-bar--active {
  background: #2e7d32;
}

.password-score-reasons {
  min-height: 2.4em;
  line-height: 1.2em;
  font-size: 0.82rem;
  color: #455a64;
}

.button-text-disabled-strike {
  text-decoration: line-through;
  opacity: 0.75;
}

.password-hint-text {
  text-decoration: none !important;
  color: #37474f;
  line-height: 1.28;
}

.password-hint-block {
  width: 100%;
  padding-left: 40px;
  font-size: 0.75rem;
}

.password-hint-link {
  appearance: none;
  border: 0;
  background: transparent;
  padding: 0;
  margin-left: 0.35rem;
  color: #f4511e;
  text-decoration: underline;
  cursor: pointer;
  font: inherit;
  line-height: inherit;
}

.password-hint-link:disabled {
  opacity: 0.6;
  cursor: default;
}

@media (max-width: 600px) {
  .language-switcher {
    top: 10px;
    right: 10px;
  }
}

@keyframes waveShiftA {
  from {
    background-position: 0 0;
  }
  to {
    background-position: 240px 0;
  }
}

@keyframes waveShiftB {
  from {
    background-position: 120px 22px;
  }
  to {
    background-position: -120px 22px;
  }
}
</style>
